import React, { CSSProperties, ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AntdTimeline from 'antd/lib/timeline';
import { childrenOfType } from '../../../utils';
import classnames from 'classnames';
import { generateUUID } from '@unifocus/js-utils';

/**
 * {@docCategory Timeline}
 */
export interface TimelineProps {
   /**
    * By sending alternate the timeline will distribute the nodes to the left and right.
    * @defaultvalue left
    */
   mode?: 'left' | 'right' | 'alternate';

   /**
    * Set the last ghost node's existence or its content.
    * @defaultvalue false
    */
   pending?: boolean;

   /**
    * Set the dot of the last ghost node when pending is true.
    * @defaultvalue <LoadingOutlined/>
    */
   pendingDot?: ReactNode;

   /**
    * Whether reverse nodes or not
    * @defaultvalue false
    */
   reverse?: boolean;

   /**
    * Customize the timeline dot background color default.  If not set, it will try to compute the parent
    * background color.  If it cannot compute, it will be white.
    */
   defaultDotBackgroundColor?: string;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * The timeline items.
    */
   children?: ReactNode;
}

/**
 * {@docCategory Timeline}
 */
export interface TimelineItemProps {
   /**
    * Set the circle's color to blue, red, green, gray or other custom colors.
    * @defaultvalue blue
    */
   color?: string;

   /**
    * Set the label
    */
   label?: ReactNode;

   /**
    * Customize node position.
    */
   position?: 'left' | 'right';

   /**
    * Customize timeline dot
    */
   dot?: React.ReactNode

   /**
    * Customize the timeline dot background color.
    */
   dotBackgroundColor?: string;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * The content of the timeline item.
    */
   children?: ReactNode;
}

export const Timeline = (props: TimelineProps) => {

   const classNameWithUUIDRef = useRef('uf-timeline-'+generateUUID());

   const className = classnames('uf-timeline', classNameWithUUIDRef.current, props.className);
   const [dotBackgroundColor, setDotBackgroundColor] = useState<string | undefined>(props.defaultDotBackgroundColor);

   useLayoutEffect(() => {
      if (dotBackgroundColor === undefined) {
         const timelineElement = document.documentElement.querySelector('.' + classNameWithUUIDRef.current);
         if (timelineElement) {
            setDotBackgroundColor(getInheritedBackgroundColor(timelineElement));
         }
      }
   },[classNameWithUUIDRef, dotBackgroundColor]);

   return (
      <AntdTimeline
         mode={props.mode}
         pending={props.pending}
         pendingDot={props.pendingDot}
         reverse={props.reverse}
         className={className}
         style={props.style}
      >
         {mapChildren(props, dotBackgroundColor)}
      </AntdTimeline>
   );
};

export class TimelineItem extends React.Component<TimelineItemProps> {
}

const mapChildren = (props: TimelineProps, dotBackgroundColor?: string) => {
   return childrenOfType(props.children, TimelineItem).map(((value, index) => {
      const itemProps = value.props as TimelineItemProps;
      const { dotBackgroundColor: dotBackgroundColorProp, className: classNameProp, color, label, position, dot, style: styleProp } = itemProps;

      const className = classnames('uf-timeline-item', classNameProp);

      const bgColor = dotBackgroundColorProp || dotBackgroundColor;

      const style: any = {
         "--dot-background-color": bgColor,
         ...styleProp
      };

      return (
         <AntdTimeline.Item
            key={index}
            color={color}
            label={label}
            position={position}
            dot={dot}
            style={style}
            className={className}
         >
            {itemProps.children}
         </AntdTimeline.Item>
      );
   }));
};


const getDefaultBackground = () => {

   const div = document.createElement("div");
   document.head.appendChild(div);
   const bg = window.getComputedStyle(div).backgroundColor;
   document.head.removeChild(div);
   return bg;
};

const getInheritedBackgroundColor = (el: Element): string => {

   // get default style for current browser
   const defaultStyle = getDefaultBackground();

   // get computed color for el
   const backgroundColor = window.getComputedStyle(el).backgroundColor;

   // if we got a real value, return it
   if (backgroundColor != defaultStyle) {
      return backgroundColor;
   }

   // if we've reached the top parent el without getting an explicit color, return default
   if (!el.parentElement) {
      return defaultStyle;
   }

   // otherwise, recurse and try again on parent element
   return getInheritedBackgroundColor(el.parentElement);
};
